import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import Select, { ActionMeta } from 'react-select';
import LoadingSpinner from '../loading-spinner/loadingSpinner';
import './dropdown.scss';

const Dropdown = ({
    initialOptions,
    onSelect,
    placeholder,
    placeholderTitle,
    id,
    isDisabled,
    selectValue,
    originalOptions = null,
    separatorIndex = 0,
    isLoading = false,
    isUFselect = false,
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isFocusedModal, setIsFocusedModal] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [options, setOptions] = useState([]);

    const initialOptionsFiltered = useMemo(
        () => [...new Map(initialOptions?.map((item) => [item.value, item])).values() as any],
        [initialOptions],
    );

    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);

        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    useEffect(() => {
        setOptions(initialOptionsFiltered as any);
    }, [initialOptionsFiltered]);

    useEffect(() => {
        if (isFocused && isMobile) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isFocused, isMobile]);

    const handleFocus = () => setIsFocused(true);
    const handleFocusModal = () => setIsFocusedModal(true);
    const handleBlur = () => setIsFocused(false);
    const handleBlurModal = () => setIsFocusedModal(false);
    const handleChange = (
        selectedOption: ((newValue: any, actionMeta: ActionMeta<any>) => void) | undefined | null,
    ) => {
        onSelect(selectedOption);
        setIsFocused(!!selectedOption);
    };
    const handleChangeModalOptions = (
        selectedOption: ((newValue: any, actionMeta: ActionMeta<any>) => void) | undefined | null,
    ) => {
        onSelect(selectedOption);
        setIsFocused(false);
        setIsFocusedModal(false);
    };

    const handleInputChange = (value: string) => {
        if (originalOptions && value) {
            setOptions(originalOptions);
        } else {
            setOptions(initialOptionsFiltered as any);
        }
        setInputValue(value);
    };

    const dropdownResultPageChangeUF = {
        control: {
            outline: 'none',
            border: 'none',
            display: 'inline-flex',
            width: '150px',
            height: 'fit-content',
            background: 'none',
            padding: '0',
            boxShadow: 'none',
        },
        input: {
            display: 'inline-block'
        },
        valueContainer: {
            padding: '0',
            fontWeight: '600',
            fontSize: '14px',
        },
        container: {
            position: 'unset',
        },
    };

    const dropdownDefaultCustomStyles = {
        control: (state: { isFocused: boolean }) => ({
            outline: state.isFocused ? '1px solid #2E2D37' : '0px solid #2E2D37',
            border: '1px solid #2E2D37',
            width: '312px',
            height: '48px',
            borderRadius: '8px',
        }),
    };

    const customStyles = {
        control: (provided: any, state: { isFocused: any }) => ({
            ...provided,
            paddingInline: '10px',
            boxShadow: state.isFocused ? '0 0 0 1px #2E2D37' : 'none',
            '&:hover': {
                cursor: 'pointer',
            },
            position: 'relative',
            ...isUFselect ? dropdownResultPageChangeUF.control : dropdownDefaultCustomStyles.control(state),
        }),
        option: (
            provided: any,
            state: {
                data(data: any): unknown;
                selectProps: any;
                isFocused: any;
                isSelected: any;
            },
        ) => ({
            ...provided,
            zIndex: 2,
            borderBottom:
                separatorIndex > 0 && !inputValue && state.selectProps.options.indexOf(state.data) === separatorIndex
                    ? '1px solid #EDECF2'
                    : 'none',
            backgroundColor: state.isSelected ? '#EDECF2' : state.isFocused ? '#EDECF2' : 'white',
            color: '#2E2D37',
            '&:hover': {
                backgroundColor: '#EDECF2',
            },
            marginBottom: '4px',
            borderRadius: '8px',
            padding: '8px',
            cursor: 'pointer',
            width: 'calc(100% - 8px)',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        dropdownIndicator: (provided: any, state: any) => ({
            ...provided,
            color: '#6A6977',
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
            transition: 'transform 0.2s ease',
        }),
        menu: (provided: any) => ({
            ...provided,
            zIndex: 5,
            left: '0',
            boxShadow: isFocused && isMobile ? 'none' : '0px 8px 16px 0px rgba(46, 45, 55, 0.12)',
        }),
        container: (provided: any) => ({
            ...provided,
            ...isUFselect ? dropdownResultPageChangeUF.container : {},
        }),
        valueContainer: (provided: any) => ({
            ...provided,
            ...isUFselect ? dropdownResultPageChangeUF.valueContainer : {},
        }),
    };

    const ClearIndicator = (props) => {
        const {
            children = (
                <Image
                    alt="Ícone de limpar"
                    title="Limpar"
                    width={10}
                    height={10}
                    src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-closed.svg`}
                />
            ),
            getStyles,
            innerProps: { ref, ...restInnerProps },
        } = props;
        return (
            <div {...restInnerProps} ref={ref} style={getStyles('clearIndicator', props)}>
                {children}
            </div>
        );
    };

    return (
        <>
            <label
                htmlFor={id}
                data-testid={`${id}-label`}
                id={`${id}-label`}
                style={{
                    position: 'absolute',
                    top: isFocused || selectValue ? '-7px' : '16px',
                    left: '10px',
                    fontSize: isFocused || selectValue ? '12px' : '14px',
                    backgroundColor: 'white',
                    paddingInline: isFocused || selectValue ? '3px' : '0px',
                    marginInline: isFocused || selectValue ? '7px' : '10px',
                    marginBottom: '-10px',
                    color: isDisabled ? '#D0CFDA' : '#2E2D37',
                    transition: 'top 0.2s, font-size 0.2s',
                    pointerEvents: 'none',
                    zIndex: 1,
                }}
                className="custom-placeholder"
            >
                {placeholder}
            </label>

            {!isMobile ? (
                <>
                    {isUFselect ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M12 10.9996C11.449 10.9996 11 10.5506 11 9.9996C11 9.4486 11.449 8.9996 12 8.9996C12.551 8.9996 13 9.4486 13 9.9996C13 10.5506 12.551 10.9996 12 10.9996ZM12 6.9996C10.346 6.9996 9 8.3456 9 9.9996C9 11.6536 10.346 12.9996 12 12.9996C13.654 12.9996 15 11.6536 15 9.9996C15 8.3456 13.654 6.9996 12 6.9996ZM12 19.733C10.391 18.422 6 14.438 6 10C6 6.691 8.691 4 12 4C15.309 4 18 6.691 18 10C18 14.438 13.609 18.422 12 19.733ZM12 2C7.589 2 4 5.588 4 10C4 16.44 11.116 21.597 11.419 21.814C11.593 21.938 11.796 22 12 22C12.204 22 12.407 21.938 12.581 21.814C12.884 21.597 20 16.44 20 10C20 5.588 16.411 2 12 2Z" fill="#6A6977" />
                        </svg>
                    ) : null}
                    <Select
                        instanceId={id}
                        value={selectValue}
                        isDisabled={isDisabled}
                        placeholder={''}
                        className={`select-dropdown-input-filter ${isDisabled ? 'disabled' : ''}`}
                        classNamePrefix="custom-select"
                        noOptionsMessage={() => 'Nenhum resultado encontrado'}
                        options={options || []}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        onInputChange={handleInputChange}
                        styles={customStyles}
                        aria-labelledby={`${id}-label`}
                        isSearchable={true}
                        menuShouldScrollIntoView={false}
                        isLoading={isLoading}
                        components={{
                            LoadingIndicator: () => <LoadingSpinner />,
                        }}
                        data-testid={`${id}-select`}
                        id={id}
                    />
                </>
            ) : (
                <>
                    <button
                        className="container-input-modal"
                        onClick={() => !isDisabled && setIsFocused(true)}
                        onKeyDown={() => !isDisabled && setIsFocused(true)}
                        data-testid={`${id}-modal-button`}
                        id={`${id}-modal-button`}
                    >
                        <div className="content-input-modal">
                            {isUFselect ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M12 10.9996C11.449 10.9996 11 10.5506 11 9.9996C11 9.4486 11.449 8.9996 12 8.9996C12.551 8.9996 13 9.4486 13 9.9996C13 10.5506 12.551 10.9996 12 10.9996ZM12 6.9996C10.346 6.9996 9 8.3456 9 9.9996C9 11.6536 10.346 12.9996 12 12.9996C13.654 12.9996 15 11.6536 15 9.9996C15 8.3456 13.654 6.9996 12 6.9996ZM12 19.733C10.391 18.422 6 14.438 6 10C6 6.691 8.691 4 12 4C15.309 4 18 6.691 18 10C18 14.438 13.609 18.422 12 19.733ZM12 2C7.589 2 4 5.588 4 10C4 16.44 11.116 21.597 11.419 21.814C11.593 21.938 11.796 22 12 22C12.204 22 12.407 21.938 12.581 21.814C12.884 21.597 20 16.44 20 10C20 5.588 16.411 2 12 2Z" fill="#6A6977" />
                                </svg>
                            ) : null}
                            <span
                                className="select-value-text"
                                data-testid={`${id}-select-value`}
                                id={`${id}-select-value`}
                            >
                                {selectValue ? selectValue.label : ''}
                            </span>

                            {isLoading && (
                                <LoadingSpinner />
                            )}

                            <div
                                style={{ marginLeft: 'auto', opacity: isDisabled ? 0.36 : 1 }}
                                data-testid={`${id}-arrow-icon`}
                                id={`${id}-arrow-icon`}
                            >
                                <Image
                                    alt="Ícone de seta para baixo"
                                    title="Seta para baixo"
                                    width={24}
                                    height={24}
                                    src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-arrow-down.svg`}
                                />
                            </div>
                        </div>
                    </button>
                    <Modal
                        isOpen={isFocused && isMobile}
                        onRequestClose={() => setIsFocused(false)}
                        style={{
                            content: {
                                top: '0',
                                left: '0',
                                right: '0',
                                bottom: '0',
                                padding: '0',
                                backgroundColor: 'white',
                                zIndex: '10000',
                            },
                            overlay: {
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                zIndex: '9999',
                            },
                        }}
                        ariaHideApp={false}
                        data-testid={`${id}-modal`}
                        id={`${id}-modal`}
                    >
                        <div className="title-modal" data-testid={`${id}-modal-title`} id={`${id}-modal-title`}>
                            <h3>{placeholderTitle}</h3>
                            <button
                                onClick={() => setIsFocused(false)}
                                data-testid={`${id}-modal-close-button`}
                                id={`${id}-modal-close-button`}
                            >
                                <Image
                                    alt="Ícone de limpar"
                                    title="Limpar"
                                    width={18}
                                    height={18}
                                    src={`${process.env.NEXT_PUBLIC_ASSETS_PATH}/assets/img/icon/icon-closed.svg`}
                                />
                            </button>
                        </div>

                        <div className="content-modal" data-testid={`${id}-modal-content`} id={`${id}-modal-content`}>
                            <label
                                htmlFor={id}
                                data-testid={`${id}-modal-label`}
                                id={`${id}-modal-label`}
                                style={{
                                    position: 'relative',
                                    top: isFocusedModal || selectValue ? '9px' : '32px',
                                    left: '10px',
                                    fontSize: isFocusedModal || selectValue ? '12px' : '14px',
                                    backgroundColor: 'white',
                                    paddingInline: isFocusedModal || selectValue ? '3px' : '0px',
                                    marginInline: isFocusedModal || selectValue ? '7px' : '10px',
                                    marginBottom: '-10px',
                                    color: isDisabled ? '#D0CFDA' : '#2E2D37',
                                    transition: 'top 0.2s, font-size 0.2s',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                }}
                                className="custom-placeholder"
                            >
                                {placeholder}
                            </label>
                            <Select
                                instanceId={id}
                                value={selectValue}
                                isDisabled={isDisabled}
                                className="select-modal-input"
                                classNamePrefix="custom-select"
                                placeholder={''}
                                noOptionsMessage={() => 'Nenhum resultado encontrado'}
                                options={options || []}
                                onChange={handleChangeModalOptions}
                                onInputChange={handleInputChange}
                                styles={customStyles}
                                aria-labelledby={`${id}-modal-label`}
                                isSearchable={true}
                                onFocus={handleFocusModal}
                                onBlur={handleBlurModal}
                                autoFocus
                                components={{
                                    LoadingIndicator: () => <LoadingSpinner />,
                                    ClearIndicator,
                                    DropdownIndicator: () => null,
                                }}
                                isClearable
                                menuIsOpen
                                menuShouldScrollIntoView={false}
                                isLoading={isLoading}
                                data-testid={`${id}-modal-select`}
                                id={`${id}-modal-select`}
                            />
                        </div>
                    </Modal>
                </>
            )}
        </>
    );
};

export default Dropdown;
